import { CheckCircleIcon, CheckIcon, MinusIcon } from '@heroicons/react/20/solid';
import customFetch from '@utils/customFetch';
import { Link, redirect, useLoaderData } from 'react-router-dom';
import SEO from '@utils/seo';
import {
  COMPARISON,
  FAQ,
  HOW_IT_WORKS,
  LISTING_IMAGE,
  PRODUCT_IDS,
  TRUST_ITEMS,
  absoluteUrl,
  formatPrice,
  getProductContent,
} from './produktContent';
import ProductFaq from './ProductFaq';
import BusinessTeaser from './BusinessTeaser';

export const loader = async () => {
  try {
    const { data } = await customFetch.get('/products/');
    return data;
  } catch (error) {
    return redirect('/');
  }
};

// Zelle der Vergleichstabelle: Häkchen, Strich oder Text.
const ComparisonCell = ({ value }) => {
  if (value === true) {
    return (
      <CheckIcon
        className="mx-auto h-5 w-5 text-svisit-primary-600"
        role="img"
        aria-label="inklusive"
      />
    );
  }
  if (value === false) {
    return (
      <MinusIcon
        className="mx-auto h-5 w-5 text-gray-300"
        role="img"
        aria-label="nicht enthalten"
      />
    );
  }
  return <span className="text-sm text-gray-700">{value}</span>;
};

const ProductGrid = () => {
  const data = useLoaderData();
  // Günstigstes zuerst (Basic → Custom), wie auf svisit.ch/preise.
  const products = [...data.products].sort((a, b) => a.price - b.price);
  const basic = products.find((p) => p._id === PRODUCT_IDS.basic);
  const custom = products.find((p) => p._id === PRODUCT_IDS.custom);

  return (
    <main className="py-24">
      <SEO
        title="Produkte von sVisit - Die digitale Visitenkarte."
        description="NFC-Visitenkarten von sVisit: Basic-Karte im sVisit-Design für CHF 15 oder Custom-Karte beidseitig personalisiert für CHF 40 – einmalig, kein Abo. Gedruckt in St. Gallen."
        type="website"
        image={absoluteUrl(LISTING_IMAGE)}
      />

      {/* Hero */}
      <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <p className="text-base font-semibold text-svisit-primary-600">
          NFC-Visitenkarten
        </p>
        <h1 className="mt-2 text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
          Eine Karte. Ein Profil. Einmalig bezahlen.
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
          Wähle die Basic-Karte im sVisit-Design oder gestalte deine
          Custom-Karte beidseitig selbst. Beide öffnen dein digitales Profil –
          ohne App, jederzeit änderbar.
        </p>
        <ul
          role="list"
          className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-600"
        >
          {TRUST_ITEMS.map((item) => (
            <li key={item} className="flex items-center">
              <CheckCircleIcon
                className="mr-1.5 h-5 w-5 text-svisit-primary-600"
                aria-hidden="true"
              />
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Produktkarten */}
      <section
        aria-labelledby="products-heading"
        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8"
      >
        <h2 id="products-heading" className="sr-only">
          Unsere Karten
        </h2>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {products.map((product) => {
            const content = getProductContent(product);
            const href = `/produkte/${product._id}`;
            return (
              <article
                key={product._id}
                className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xs"
              >
                <Link
                  to={href}
                  className="relative block aspect-[3/2] w-full overflow-hidden bg-gray-50"
                >
                  <img
                    src={content.gallery[0].src}
                    alt={content.gallery[0].alt}
                    className="h-full w-full object-cover object-center"
                  />
                  {content.badge && (
                    <span className="absolute left-4 top-4 rounded-full bg-svisit-primary-600 px-3 py-1 text-xs font-semibold text-white">
                      {content.badge}
                    </span>
                  )}
                </Link>
                <div className="flex flex-1 flex-col p-6 sm:p-8">
                  <h3 className="text-2xl font-bold tracking-tight text-gray-900">
                    <Link to={href}>{product.name}</Link>
                  </h3>
                  <p className="mt-2 text-base text-gray-600">
                    {content.tagline}
                  </p>
                  {content.features.length > 0 && (
                    <ul role="list" className="mt-6 space-y-2">
                      {content.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start text-sm text-gray-700"
                        >
                          <CheckIcon
                            className="mr-2 mt-0.5 h-5 w-5 shrink-0 text-svisit-primary-600"
                            aria-hidden="true"
                          />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-8 flex items-end justify-between border-t border-gray-100 pt-6">
                    <div>
                      <p className="text-3xl font-bold tracking-tight text-gray-900">
                        {formatPrice(product.price)}
                      </p>
                      <p className="text-sm text-gray-500">einmalig, kein Abo</p>
                    </div>
                    <Link
                      to={href}
                      className="rounded-md bg-svisit-primary-600 px-5 py-3 text-base font-semibold text-white shadow-xs hover:bg-svisit-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-600"
                    >
                      {content.cta}
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Vergleich */}
      {basic && custom && (
        <section
          aria-labelledby="comparison-heading"
          className="mx-auto mt-24 max-w-5xl px-4 sm:px-6 lg:px-8"
        >
          <h2
            id="comparison-heading"
            className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
          >
            Basic oder Custom?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-lg text-gray-600">
            Beide Karten öffnen dasselbe digitale Profil. Der Unterschied liegt
            im Design der Karte.
          </p>
          <div className="mt-10 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left">
              <thead>
                <tr>
                  <th scope="col" className="py-3 pr-4 text-sm font-semibold text-gray-900">
                    Leistung
                  </th>
                  {[basic, custom].map((product) => (
                    <th
                      key={product._id}
                      scope="col"
                      className="px-4 py-3 text-center text-sm font-semibold text-gray-900"
                    >
                      <Link to={`/produkte/${product._id}`} className="hover:text-svisit-primary-600">
                        {getProductContent(product).shortName}
                      </Link>
                      <span className="block text-xs font-normal text-gray-500">
                        {formatPrice(product.price)} einmalig
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {COMPARISON.map((row) => (
                  <tr key={row.label}>
                    <th scope="row" className="py-3 pr-4 text-sm font-normal text-gray-700">
                      {row.label}
                    </th>
                    <td className="px-4 py-3 text-center">
                      <ComparisonCell value={row.basic} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <ComparisonCell value={row.custom} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* So funktioniert's */}
      <section
        aria-labelledby="how-heading"
        className="mx-auto mt-24 max-w-5xl px-4 sm:px-6 lg:px-8"
      >
        <h2
          id="how-heading"
          className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
        >
          So funktioniert&apos;s
        </h2>
        <ol className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-3">
          {HOW_IT_WORKS.map((step, index) => (
            <li key={step.title}>
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-svisit-primary-600 text-base font-bold text-white">
                {index + 1}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                {step.title}
              </h3>
              <p className="mt-2 text-base text-gray-600">{step.text}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Business */}
      <div className="mx-auto mt-24 max-w-5xl px-4 sm:px-6 lg:px-8">
        <BusinessTeaser />
      </div>

      {/* FAQ (Auszug) */}
      <div className="mx-auto mt-24 max-w-3xl px-4 sm:px-6 lg:px-8">
        <ProductFaq items={FAQ.slice(0, 5)} />
      </div>
    </main>
  );
};

export default ProductGrid;
